/**
 * StatCard — the KPI tile.
 *
 * Locked in the reference system: uppercase label, big value, optional trend and
 * hint. **No icon** — one design used on every dashboard.
 *
 * `trend` accepts two shapes:
 *   • the pill chip — `{ tone: "up" | "down" | "flat"; label }` — a tinted rounded
 *     badge next to the value (the desk's health chips);
 *   • the directional delta — `{ direction: "up" | "down"; value; goodWhenDown? }` —
 *     an arrow + coloured value, green for the good direction, red for the bad one.
 *     Set `goodWhenDown` for cost-type metrics where a fall is the win.
 */
import { Show, type JSX } from "solid-js";
import { Dynamic } from "solid-js/web";
import ArrowUp from "lucide-solid/icons/arrow-up";
import ArrowDown from "lucide-solid/icons/arrow-down";
import { cn } from "./utils/cn";
import { Skeleton } from "./skeleton";

export type TrendTone = "up" | "down" | "flat";

/** The tinted pill chip. */
export type TrendChip = { tone: TrendTone; label: string };
/** The directional delta (arrow + coloured value). */
export type TrendDelta = { direction: "up" | "down"; value: JSX.Element; goodWhenDown?: boolean };
export type StatTrend = TrendChip | TrendDelta;

const TREND: Record<TrendTone, string> = {
  up: "bg-[var(--c9-bg-success)] text-[color:var(--c9-text-success)]",
  down: "bg-[var(--c9-bg-danger)] text-[color:var(--c9-text-danger)]",
  flat: "bg-[var(--c9-bg-neutral)] text-[color:var(--c9-text-subtle)]",
};

const isDelta = (t: StatTrend): t is TrendDelta => "direction" in t;

export function StatCard(props: {
  label: string;
  value: string;
  hint?: string;
  trend?: StatTrend;
  class?: string;
}): JSX.Element {
  /** For the delta shape: is this the metric's "good" direction? */
  const positive = (t: TrendDelta) => (t.goodWhenDown ? t.direction === "down" : t.direction === "up");

  return (
    <div
      class={cn(
        "flex min-w-0 flex-col rounded-large border border-[color:var(--c9-border)]",
        "bg-[var(--c9-surface)] p-250",
        props.class,
      )}
    >
      <p class="truncate text-body-s font-[700] uppercase tracking-[0.06em] text-[color:var(--c9-text-subtlest)]">
        {props.label}
      </p>
      <div class="mt-100 flex items-baseline gap-100">
        <p class="min-w-0 truncate text-h-l font-[670] tabular-nums text-[color:var(--c9-text)]">
          {props.value}
        </p>
        <Show when={props.trend}>
          {(trend) => (
            <Show
              when={isDelta(trend())}
              fallback={
                <span
                  class={cn(
                    "shrink-0 rounded-full px-100 py-025 text-body-s font-[630] tabular-nums",
                    TREND[(trend() as TrendChip).tone],
                  )}
                >
                  {(trend() as TrendChip).label}
                </span>
              }
            >
              <span
                class="inline-flex shrink-0 items-center gap-025 text-body-s font-[630] tabular-nums"
                style={{
                  color: positive(trend() as TrendDelta)
                    ? "var(--c9-text-success)"
                    : "var(--c9-text-danger)",
                }}
              >
                <Dynamic
                  component={(trend() as TrendDelta).direction === "up" ? ArrowUp : ArrowDown}
                  size={14}
                />
                {(trend() as TrendDelta).value}
              </span>
            </Show>
          )}
        </Show>
      </div>
      <Show when={props.hint}>
        <p class="mt-050 truncate text-body-s text-[color:var(--c9-text-subtlest)]">{props.hint}</p>
      </Show>
    </div>
  );
}

/** Reserves the exact height of a loaded StatCard — loading must not shift layout. */
StatCard.Skeleton = function StatCardSkeleton(props: { class?: string }): JSX.Element {
  return (
    <div
      class={cn(
        "flex min-w-0 flex-col rounded-large border border-[color:var(--c9-border)]",
        "bg-[var(--c9-surface)] p-250",
        props.class,
      )}
    >
      <Skeleton class="h-4 w-24" />
      <Skeleton class="mt-100 h-7 w-32" />
      <Skeleton class="mt-050 h-4 w-20" />
    </div>
  );
};
